import type { SagaReturnType } from 'redux-saga/effects';
import {
  all,
  call,
  delay,
  put,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';
import URIjs from 'urijs';

import { logInAction } from 'src/store/client/actions';
import { currentUri as URI, getUrlParams, updateURL } from 'src/utils/uriUtils';

import { api } from 'src/api';
import { INIT_APP } from 'src/store/view/constants';

import { getIsMobile, searchTypeSelector } from 'src/store/view/selectors';
import { scrollToElement } from 'src/utils/domUtils';
import { enqueueDTO } from 'src/store/enqueue/enqueueDTO';
import { logErrorToSentryDTOError } from 'src/error/utils/logErrorToSentry';
import { getRecommenderTypeForSearchPages } from 'src/utils/featureToggleSigma/analytics/abNewRecommender';
import { uniq } from 'lodash';
import {
  CHANGE_SEARCH_PARAMS,
  SEARCH_ENRICHED,
  SEARCH_SUCCESS,
  SEARCH_TIMED_OUT,
  START_SEARCH_PROCESS,
} from '../constants/search';
import { ERRORS_LIMIT } from '../searchStatus/constants';
import { getRequestId } from '../selectors/searchParams';
import {
  enreachErrorsLimitSelector,
  getOperatorsStatus,
  hasResults,
  isSearchByFiltersSelector,
  isSearchCompleteSelector,
  isSearchDone,
  searchStatusErrorsCountSelector,
  sizeSearchSelector,
} from '../searchStatus/selectors';
import {
  hotelsHasPaginationSelector,
  hotelsPageNumberSelector,
  hotelsVisibilitySelector,
  latestVisibleHotelSelector,
  penalizedHotelsVisibilitySelector,
} from '../store/hotels/selectors';
import {
  setCurrentVisibleHotels,
  setCurrentVisiblePenalizedHotels,
  setHotels,
  setTopHotelData,
} from '../store/hotels/slice';

import {
  searchEnrichedtAction,
  searchSuccessAction,
  searchTimedOutAction,
  startSearchAction,
} from '../actions/search';
import { toggleFiltersAction } from '../actions/view';
import { getMapDataAction } from '../store/map/sagaActions';

import type { StatusResponce } from '../store/search/types';

import { getCCXUUID, isEqualStatuses, makeSearchStatusParams } from '../utils';

import {
  setIsEmptyFiltration,
  setIsNotEmptyFiltration,
} from '../store/emptyFiltration/slices';
import { setIsEmptySearch } from '../store/emptySearch/slices';

import {
  ENRICHED_DELAY,
  SEARCH_TIMEOUT,
  STATUS_INTERVAL,
} from '../../../constants';
import {
  getFilters,
  isMobileHotelFilterOpenedSelector,
} from '../selectors/filtersBase';
import { PAGINATION } from '../store/hotels/constants';
import { isEmptySearchWithFiltersSelector } from '../store/emptyFiltration/selectors';
import {
  operatorsStatusChanged,
  resetIsSearchByFilters,
  setHasMoreResults,
} from '../searchStatus/slice';
import {
  getPreviousFiltersAction,
  setPreviousFiltersAction,
} from '../../../store/previousFilters/actions';
import { setIsPreviousFiltersShown } from '../../../store/previousFilters/slice';
import { isSuggestedFiltersShownSelector } from '../../../store/previousFilters/selectors';
import { getSortField, getSortOrder } from '../selectors/sorting';
import { filterDataToURL } from '../controllers/filtersController';

const SORTING_PARAM = 'sort_by';

export function* getSortParams() {
  const field = yield select(getSortField);
  const order = yield select(getSortOrder);
  return { [SORTING_PARAM]: order ? `${field},${order}` : field };
}

export function* getFiltersParams() {
  const filtersData = yield select(getFilters);
  const urlData = yield getUrlParams();
  const stateFilters = filtersData.keySeq().reduce((result, filterName) => {
    const filterData = filtersData.get(filterName);
    if (filterData) {
      const filterParams = filterDataToURL(filterName, filterData);
      Object.keys(filterParams).forEach((param) => {
        const val = filterParams[param];
        if (val) {
          // eslint-disable-next-line no-param-reassign
          result[param] = val;
        }
      });
    }
    return result;
  }, {});

  const allFilters = { ...stateFilters, ...urlData };

  /*
    allFilters являются мёрджем двух фильтров: из url и из стора.
    stateFilters[key] и urlData[key] это строки формата 'val1,val2' и 'val2,val4', 
    а мы хотим получить 'val1,val2,val4', для чего и ищем поля, которые есть и у stateFilters, и у urlData,
    используем конкатенацию, преобразование к array
    и дальнейшее вычисление уникальных значений
  */

  Object.keys(allFilters).forEach((key) => {
    if (stateFilters[key] && urlData[key])
      allFilters[key] = uniq(
        `${stateFilters[key]},${urlData[key]}`.split(','),
      ).join(',');
  });

  return allFilters;
}

function* handleStartSearch() {
  yield put(startSearchAction());
}

function* statusRequest() {
  const requestId = yield select(getRequestId);

  const data: StatusResponce = yield api.getSearchStatus({
    request_id: requestId,
  });
  return data;
}

function* refreshSession() {
  const requestId = yield select(getRequestId);
  const errorsCount = yield select(searchStatusErrorsCountSelector);

  try {
    if (errorsCount >= ERRORS_LIMIT) {
      throw new Error(
        'cant refresh sesion: get_grouped_hotels finished with error',
      );
    }
    const response = enqueueDTO(
      yield api.getSearchEnqueue({
        request_id: requestId,
      }),
    );

    yield put(
      startSearchAction({
        requestId: response.request_id,
        searchParams: response.search_params,
        errorsCount: errorsCount + 1,
      }),
    );
  } catch (error) {
    yield put(searchTimedOutAction());
  }
}

function* getFilteredResults() {
  const requestId = yield select(getRequestId);
  const pageNumber = yield select(hotelsPageNumberSelector);
  const hasPagination = yield select(hotelsHasPaginationSelector);
  // Хак, так как url не успевает обновиться после изменений фильтров
  yield delay(100);
  /**
   * Для фильтра по названию отеля в мобильной версии
   * не нужно прокидывать остальные фильтры
   */

  const filters = yield getFiltersParams();
  const sorting = yield getSortParams();
  const ccxuuid = getCCXUUID();
  const urlParams = getUrlParams();

  const isMobile = yield select(getIsMobile);
  const searchType = yield select(searchTypeSelector);

  const { type }: SagaReturnType<typeof getRecommenderTypeForSearchPages> =
    yield call(getRecommenderTypeForSearchPages, isMobile, searchType);

  const resp: SagaReturnType<typeof api.getSearchGetGroupedHotels> =
    yield api.getSearchGetGroupedHotels({
      ...filters,
      ...sorting,
      request_id: requestId,
      ccx_uuid: ccxuuid,
      page_limit: hasPagination ? PAGINATION.PAGE_LIMIT : undefined,
      page_number: hasPagination ? pageNumber : undefined,
      // поле определяющее какой использовать рекомендатор
      engine: type === 'new' ? 'new' : 'hotelrank',
      top_hotel_id: urlParams.top_hotel_id,
    });

  return resp;
}

function* scrollToPosition(newHotelsCount: number) {
  const latestVisibleHotel = yield select(latestVisibleHotelSelector);
  const isSearchProgressCompleted = yield select(isSearchCompleteSelector);
  const areHotelsVisible = yield select(hotelsVisibilitySelector);
  const arePenalizedHotelsVisible = yield select(
    penalizedHotelsVisibilitySelector,
  );
  const isSearchByFilters = yield select(isSearchByFiltersSelector);

  const allHotelsAreNotVisible =
    !areHotelsVisible && !arePenalizedHotelsVisible;

  /**
   * очень большая и сложная логика правильного срабатывания скролла
   * https://tracker.yandex.ru//LT-34162
   */
  const shouldScroll =
    (arePenalizedHotelsVisible ||
      (allHotelsAreNotVisible && newHotelsCount < latestVisibleHotel) ||
      (areHotelsVisible && arePenalizedHotelsVisible)) &&
    isSearchByFilters &&
    isSearchProgressCompleted;

  if (shouldScroll) {
    scrollToElement(document.body);
    yield put(setCurrentVisibleHotels([0]));
    yield put(setCurrentVisiblePenalizedHotels([0]));
  }
}

export function* fetchData() {
  try {
    const isSearchComplete = yield select(isSearchCompleteSelector);
    const hasPagination = yield select(hotelsHasPaginationSelector);
    const isMobileHotelFilter = yield select(isMobileHotelFilterOpenedSelector);
    const isMobile = yield select(getIsMobile);
    const isSearchByFilters = yield select(isSearchByFiltersSelector);
    const response = yield* getFilteredResults();
    const urlParams = getUrlParams();

    const { enriched, ...payload } = response;
    const needMergeHotels =
      hasPagination && !isMobileHotelFilter && isSearchComplete;

    if (enriched) {
      yield put(searchEnrichedtAction());
    }

    // Обработка top_hotel_name
    const topHotelId = urlParams.top_hotel_id;
    const topHotelName = response?.topHotelName || null;
    let isTopHotelNotFound = false;

    if (topHotelId && response.hotels.length > 0) {
      const firstHotelId = response.hotels[0].id;
      isTopHotelNotFound = firstHotelId !== Number(topHotelId);
    }

    yield put(
      setTopHotelData({
        topHotelName,
        isTopHotelNotFound,
      }),
    );

    yield put(searchSuccessAction(payload));
    yield put(
      setHotels({
        hotels: response.hotels,
        hotelsCount: response.hotelsCount,
        needMergeHotels,
      }),
    );

    if (!isMobile) {
      yield delay(100);
      yield scrollToPosition(payload.hotelsCount);
    }
    if (isSearchByFilters) {
      yield put(resetIsSearchByFilters());
    }
  } catch (error) {
    // TODO: handle errors here
    // это обработка на протухшую сессию.
    // Из-за jsonp-реализации запросов от других ошибок не отличить.
    // eslint-disable-next-line no-console
    console.log('error', error);
    logErrorToSentryDTOError(error);
    yield call(refreshSession);
  }
}

function updateSearchParamsInUrl(newUrl) {
  const newUri = URIjs(newUrl);
  const FLEX_DATES_PARAM = 'flex_dates';

  URI.path(newUri.path())
    .removeSearch(FLEX_DATES_PARAM)
    .addSearch(newUri.query(true));

  updateURL();
}
function* searchParamsChanged(action) {
  const { payload: searchUrl } = action;
  updateSearchParamsInUrl(searchUrl);

  const response = enqueueDTO(
    yield api.getSearchEnqueue({
      request_id: searchUrl,
    }),
  );
  const { request_id, search_params } = response;
  // TODO: think about yield put(clearFiltersAction);
  yield put(
    startSearchAction({ requestId: request_id, searchParams: search_params }),
  );
  yield put(toggleFiltersAction(false));
  yield put(getMapDataAction());

  // ensure back compabillity. some components may still use global.
  window.REQUEST_ID = request_id || '';
  window.SEARCH_PARAMS = search_params;
}

function* searchProcess(action) {
  if (action.type === SEARCH_ENRICHED) {
    yield delay(ENRICHED_DELAY);
  }
  let timeout = false;
  const searchStartTime = Date.now();
  try {
    while (!(yield select(isSearchDone))) {
      const { status, size, completeness }: StatusResponce = yield call(
        statusRequest,
      );

      const prevStatusArray = yield select(getOperatorsStatus);
      const prevSize = yield select(sizeSearchSelector);
      const currentStatusArray = Object.values(status);

      const searchStatusParams = yield makeSearchStatusParams(
        status,
        prevSize,
        size,
        completeness,
      );
      if (!isEqualStatuses(prevStatusArray, currentStatusArray))
        yield put(operatorsStatusChanged({ ...searchStatusParams, size }));

      if (size !== prevSize) {
        /**
         * Узнаем сохранены ли предыдущие фильтры у пользователя
         * для первой выдачи
         */
        const hasBeenShown = yield select(isSuggestedFiltersShownSelector);
        if (hasBeenShown === null) {
          yield put(getPreviousFiltersAction());
          yield take([
            setPreviousFiltersAction.type,
            setIsPreviousFiltersShown.type,
          ]);
        }
        yield call(fetchData);
      }

      if (!(yield select(isSearchDone))) {
        timeout = Date.now() - searchStartTime > SEARCH_TIMEOUT;

        if (timeout) {
          yield put(searchTimedOutAction());
        } else {
          yield delay(STATUS_INTERVAL);
        }
      }
    }
  } catch (error) {
    console.error('Произошла ошибка', error); // eslint-disable-line

    logErrorToSentryDTOError(error);
  }
  // если поиск завершен, новые результаты показаны не будут
  yield put(setHasMoreResults(false));
}

/**
 * Если поиск завершился и отелей не найдено,
 * определяем есть ли активные фильтры,
 * чтобы понять какой блок показывать - пустые поиски или пустую фильтрацию
 */
function* handleSearchSuccess() {
  const hasHotels = yield select(hasResults);
  const isComplete = yield select(isSearchCompleteSelector);
  const enreachErrorsLimit = yield select(enreachErrorsLimitSelector);

  if ((isComplete || enreachErrorsLimit) && !hasHotels) {
    const isEmptyFiltration = yield select(isEmptySearchWithFiltersSelector);

    if (isEmptyFiltration) {
      yield put(setIsEmptyFiltration(true));
    } else {
      yield put(setIsEmptySearch());
    }
  } else {
    yield put(setIsNotEmptyFiltration());
  }
}

export default function* searchSaga() {
  yield all([
    takeLatest([INIT_APP, logInAction.type], handleStartSearch),
    takeLatest([START_SEARCH_PROCESS, SEARCH_ENRICHED], searchProcess),
    takeLatest(
      [operatorsStatusChanged.type, SEARCH_SUCCESS, SEARCH_TIMED_OUT],
      handleSearchSuccess,
    ),
    takeLatest(CHANGE_SEARCH_PARAMS, searchParamsChanged),
  ]);
}
